import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../../../lib/api";
import {
  NO_OTP_NOT_READY_EXPLANATION,
  OTP_RISK_ACKNOWLEDGEMENT,
  OTP_SECURITY_NOTICE,
  REQUIRE_OTP_HELPER_TEXT,
  REQUIRE_OTP_LABEL,
  SHARED_BALANCE_NOTICE,
  catalogRequiresOtp,
  needsRiskAcknowledgement,
} from "../../../lib/loyaltyOtpPolicy";

/**
 * "Require OTP verification" — Catalog editor → Fulfillment Methods
 * (Oct 2026). Restores the tenant OTP control that existed only on the
 * staging integration line (PR #174) with corrected, positive wording.
 *
 * Its OWN section with its OWN Save (independent-review correction 7):
 * the order-mode checkboxes above are saved by the panel's Save, this
 * setting by the button below, through its own endpoint. A failure here
 * can never be mistaken for, or hide, a save of the order modes.
 *
 * The server is the authority for everything shown here. On open, and
 * after EVERY save attempt — success or failure — the saved value,
 * whether this user may change it, and the readiness of the mandatory
 * redemption-confirmation text are re-read from
 * `GET /retail/catalogs/{id}/loyalty-otp-policy`, so the checkbox never
 * shows a stale or unsaved state.
 *
 * Turning OTP off additionally requires (a) the risk acknowledgement and
 * (b) "Ready" — the backend enforces both; the UI only explains them.
 */
export default function LoyaltyOtpVerificationSetting({ catalog, onChanged }) {
  const catalogId = catalog?.id;
  const [savedRequired, setSavedRequired] = useState(catalogRequiresOtp(catalog));
  const [checked, setChecked] = useState(savedRequired);
  const [acknowledged, setAcknowledged] = useState(false);
  const [policy, setPolicy] = useState(null);       // GET response, or null
  const [policyError, setPolicyError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [lastResult, setLastResult] = useState(null); // {ok, message}

  const reload = useCallback(async () => {
    if (!catalogId) return null;
    try {
      const { data } = await api.get(`/retail/catalogs/${catalogId}/loyalty-otp-policy`);
      setPolicy(data);
      setPolicyError(false);
      const required = data?.require_otp_verification !== false;
      setSavedRequired(required);
      setChecked(required);
      setAcknowledged(false);
      return data;
    } catch (e) {
      setPolicy(null);
      setPolicyError(true);
      return null;
    }
  }, [catalogId]);

  useEffect(() => { reload(); }, [reload]);

  // The page reloaded the catalog (another save elsewhere): follow it.
  useEffect(() => {
    const required = catalogRequiresOtp(catalog);
    setSavedRequired(required);
    setChecked(required);
    setAcknowledged(false);
  }, [catalog?.require_otp_verification]); // eslint-disable-line react-hooks/exhaustive-deps

  const ready = policy?.no_otp_redemption_ready === true;
  const canChange = policy ? policy.can_change !== false : true;
  const needsAck = needsRiskAcknowledgement(savedRequired, checked);
  const dirty = checked !== savedRequired;
  const turningOff = savedRequired && !checked;
  const blockedByReadiness = turningOff && !ready;
  const saveDisabled = !dirty || saving || (needsAck && !acknowledged) || blockedByReadiness || !canChange;
  const notReady = (policy?.readiness || []).filter((c) => !c.ok);

  const save = async () => {
    if (saveDisabled) return;
    setSaving(true);
    setLastResult(null);
    try {
      await api.put(`/retail/catalogs/${catalogId}/loyalty-otp-policy`, {
        require_otp_verification: checked,
        risk_acknowledged: !checked && acknowledged,
      });
      setLastResult({ ok: true, message: "OTP verification setting saved." });
      toast.success("OTP verification setting saved");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const reason = (detail && typeof detail === "object" ? detail.message : detail)
        || "Please try again.";
      const message = `OTP verification setting was not saved. ${reason}`;
      setLastResult({ ok: false, message });
      toast.error(message);
    } finally {
      // Authoritative state after ANY outcome — never the optimistic one.
      await reload();
      try { await onChanged?.(); } catch (_) { /* the page reports its own reload */ }
      setSaving(false);
    }
  };

  return (
    <div
      className="mt-4 rounded-lg border border-slate-200 p-3"
      data-testid="retail-loyalty-otp-setting"
    >
      <div className="flex items-center justify-between gap-2">
        <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          Customer verification
        </div>
        <span
          data-testid="retail-no-otp-readiness"
          className={"text-xs rounded-full px-2 py-0.5 " + (ready
            ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
            : "bg-slate-100 text-slate-600 border border-slate-200")}
        >
          No-OTP loyalty redemption: {policyError ? "Status unavailable" : ready ? "Ready" : "Not ready"}
        </span>
      </div>
      <label className="mt-2 flex items-start gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={!!checked}
          disabled={saving || !canChange}
          onChange={(e) => {
            setChecked(e.target.checked);
            if (e.target.checked) setAcknowledged(false);
            setLastResult(null);
          }}
          data-testid="retail-require-otp-verification"
          className="mt-1"
        />
        <div className="flex-1">
          <div className="font-medium text-sm text-slate-900">{REQUIRE_OTP_LABEL}</div>
          <p className="text-xs text-slate-500" data-testid="retail-require-otp-verification-help">
            {REQUIRE_OTP_HELPER_TEXT}
          </p>
          <p className="mt-1 text-xs text-slate-500" data-testid="retail-require-otp-shared-balance">
            {SHARED_BALANCE_NOTICE}
          </p>
        </div>
      </label>
      {!canChange && (
        <p className="mt-2 text-xs text-slate-600" data-testid="retail-require-otp-not-permitted">
          Only the account owner or an administrator authorized to manage security settings can
          change OTP verification.
        </p>
      )}
      {!checked && (
        <div
          role="alert"
          className="mt-2 rounded-md border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900"
          data-testid="retail-require-otp-security-notice"
        >
          <p>{OTP_SECURITY_NOTICE}</p>
          <p className="mt-1" data-testid="retail-require-otp-security-notice-shared-balance">
            {SHARED_BALANCE_NOTICE}
          </p>
        </div>
      )}
      {turningOff && !ready && (
        <div
          className="mt-2 rounded-md border border-slate-300 bg-slate-50 p-2 text-xs text-slate-700"
          data-testid="retail-require-otp-not-ready"
        >
          <p className="font-medium">{NO_OTP_NOT_READY_EXPLANATION}</p>
          {notReady.length > 0 && (
            <ul className="mt-1 list-disc pl-4">
              {notReady.map((c) => <li key={c.capability}>{c.message}</li>)}
            </ul>
          )}
          {policyError && <p className="mt-1">Readiness could not be checked. Please try again.</p>}
        </div>
      )}
      {needsAck && (
        <label className="mt-2 flex items-start gap-2 text-xs text-slate-800 cursor-pointer">
          <input
            type="checkbox"
            checked={!!acknowledged}
            disabled={saving}
            onChange={(e) => setAcknowledged(e.target.checked)}
            data-testid="retail-require-otp-risk-ack"
            className="mt-0.5"
          />
          <span>{OTP_RISK_ACKNOWLEDGEMENT}</span>
        </label>
      )}
      <div className="mt-3 flex items-center justify-between gap-2">
        <div className="text-xs" data-testid="retail-require-otp-save-result">
          {lastResult && (
            <span className={lastResult.ok ? "text-emerald-700" : "text-red-700"}>
              {lastResult.message}
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={save}
          disabled={saveDisabled}
          data-testid="retail-require-otp-save"
          className={"text-xs px-3 py-1.5 rounded-md font-medium " + (saveDisabled
            ? "bg-slate-200 text-slate-500 cursor-not-allowed"
            : "bg-emerald-600 text-white hover:bg-emerald-700")}
        >
          {saving ? "Saving…" : dirty ? "Save verification setting" : "Saved"}
        </button>
      </div>
    </div>
  );
}
